export const getSettlementCurrencySymbol = (currency?: string | null): string => {
  switch (currency) {
    case 'CNY':
      return '¥';
    case 'HKD':
      return 'HK$';
    case 'CAD':
      return 'C$';
    case 'USD':
    default:
      return '$';
  }
};

const formatMajorAmount = (amount: number): string => {
  return new Intl.NumberFormat('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(amount);
};

const parseAmountValue = (amount?: string | number | null): number | null => {
  if (amount === null || amount === undefined || amount === '') {
    return null;
  }

  if (typeof amount === 'number') {
    return Number.isNaN(amount) ? null : amount;
  }

  const normalized = amount
    .trim()
    .replace(/[$¥,]/g, '')
    .replace(/HK\$/gi, '')
    .replace(/C\$/gi, '')
    .replace(/\s+/g, '');
  if (!normalized) {
    return null;
  }

  const parsed = Number(normalized);
  return Number.isNaN(parsed) ? null : parsed;
};

export const formatSettlementAmountFromCents = (
  amountInCents?: string | number | null,
  currency?: string | null,
): string => {
  if (amountInCents === null || amountInCents === undefined || amountInCents === '') {
    return '-';
  }

  const cents = parseAmountValue(amountInCents);
  if (cents === null) {
    return '-';
  }

  const symbol = getSettlementCurrencySymbol(currency);
  const absFormatted = formatMajorAmount(Math.abs(cents) / 100);
  const sign = cents < 0 ? '-' : '';
  return `${sign}${symbol} ${absFormatted}`;
};

/**
 * 无货币符号、无千分位，2 位小数，等价于老系统 `DecimalFormat("#0.00")` 直接格式化分转元的结果。
 * 仅用于历史上就没有货币符号的列（如实时收入统计-预估收入/业务实收的"收入金额"），
 * 不要用于新列——新列应统一走 {@link formatSettlementAmountFromCents}。
 */
export const formatPlainAmountFromCents = (amountInCents?: string | number | null): string => {
  if (amountInCents === null || amountInCents === undefined || amountInCents === '') {
    return '-';
  }
  const cents = parseAmountValue(amountInCents);
  if (cents === null) {
    return '-';
  }
  return (cents / 100).toFixed(2);
};

/**
 * `$` 紧贴数字、无千分位，2 位小数，等价于老系统 `"$" + DecimalFormat("#0.00").format(...)`。
 * 仅用于历史上就是这个拼接方式的列（如预收账款余额/透支额度），恒定用 `$` 不看币种——
 * 这是老系统的既有行为（原样保留，不是本次引入的新逻辑）。负数时 `$` 在负号前面
 * （如 "$-12.34"），同样是照搬老系统字符串拼接的结果，不做"修正"。
 */
export const formatDollarAmountFromCentsNoSpace = (amountInCents?: string | number | null): string => {
  if (amountInCents === null || amountInCents === undefined || amountInCents === '') {
    return '-';
  }
  const cents = parseAmountValue(amountInCents);
  if (cents === null) {
    return '-';
  }
  return `$${(cents / 100).toFixed(2)}`;
};

export const formatSettlementAmountFromMajor = (
  amount?: string | number | null,
  currency?: string | null,
): string => {
  if (amount === null || amount === undefined || amount === '') {
    return '-';
  }

  const major = parseAmountValue(amount);
  if (major === null) {
    return '-';
  }

  const symbol = getSettlementCurrencySymbol(currency);
  const absFormatted = formatMajorAmount(Math.abs(major));
  const sign = major < 0 ? '-' : '';
  return `${sign}${symbol} ${absFormatted}`;
};
export const getCurrencyName = (currency?: string | null): string => {
  switch (currency) {
    case 'USD':
      return '美元';
    case 'CNY':
      return '人民币';
    case 'HKD':
      return '港币';
    case 'CAD':
      return '加元';
    case 'EUR':
      return '欧元';
    case 'GBP':
      return '英镑';
    case 'JPY':
      return '日元';
    default:
      return currency || '-';
  }
};

